import { Injectable } from '@angular/core';
import { NbAuthService } from '@nebular/auth';
import posthog from 'posthog-js';
import { filter, switchMap, take } from 'rxjs/operators';
import { User } from '../interfaces/auth/users';
import { UserStore } from '../stores/user.store';

// PostHog is started in main.ts and records pageviews itself; this service links events to the signed-in user.
@Injectable()
export class AnalyticsService {
  constructor(private userStore: UserStore, private authService: NbAuthService) {}

  // The current-user request can return after logout has started (the logout page reloads the app),
  // so only identify while the session is still signed in
  identifyUsers() {
    this.userStore.onUserStateChange().pipe(
      filter((user: User | null) => !!user?.id),
      switchMap((user: User) => this.authService.isAuthenticated().pipe(
        take(1),
        filter((signedIn) => signedIn),
        switchMap(() => [user]),
      )),
    ).subscribe((user: User) => this.identify(user));
  }

  trackEvent(eventName: string, properties?: Record<string, any>) {
    if (posthog.__loaded) {
      posthog.capture(eventName, properties);
    }
  }

  // Next person to sign in on this browser must not inherit the last one's identity
  reset() {
    if (posthog.__loaded) {
      posthog.reset();
    }
    (window as any).Produktly?.clearCurrentUser();
  }

  // Database ID only: names and emails stay out of PostHog
  private identify(user: User) {
    if (posthog.__loaded) {
      posthog.identify(String(user.id), { role: user.role?.name });
    }
    // Instances share one Produktly token and each database numbers users from 1, so prefix the host.
    // The "Mira Admins" segment matches on roleType, which stays the same even if a role is renamed.
    (window as any).Produktly?.identifyUser(`${location.hostname}:${user.id}`, {
      role: user.role?.name,
      roleType: user.role?.type,
      instance: location.hostname,
    });
  }
}
